import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/shared/FormField';
import { CATEGORY_LABELS } from '@/components/communication/template-category.constants';
import { toast } from '@/lib/swal';
import { createMaster, type EmailTemplateMaster } from '../api/platform-email-template.api';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (created: EmailTemplateMaster) => void;
}

const KEY_RE = /^[a-z0-9]+(?:[.-][a-z0-9]+)*$/;

const SELECT_CLASS =
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';

/**
 * Create dialog (COMMUNICATION_DESIGN.md §6). Content is authored afterwards in
 * the drawer editor, so this collects identity only and creates a DRAFT.
 */
export function NewTemplateDialog({ open, onClose, onCreated }: Props) {
  const [templateKey, setTemplateKey] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('SYSTEM');
  const [subject, setSubject] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setTemplateKey('');
      setName('');
      setCategory('SYSTEM');
      setSubject('');
      setError(null);
    }
  }, [open]);

  const keyValid = KEY_RE.test(templateKey.trim());
  const canSubmit = keyValid && name.trim().length > 0 && subject.trim().length > 0 && !busy;

  const handleCreate = async () => {
    setError(null);
    setBusy(true);
    try {
      const created = await createMaster({
        templateKey: templateKey.trim(),
        name: name.trim(),
        category,
        subject: subject.trim(),
        htmlBody: '<p></p>',
        plainTextBody: '',
      });
      toast.success('Draft template created');
      onCreated(created);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create template.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="New email template" size="md">
      <div className="space-y-4">
        <FormField label="Template key" htmlFor="new-template-key" required>
          <Input
            id="new-template-key"
            value={templateKey}
            onChange={(e) => setTemplateKey(e.target.value)}
            placeholder="module.event-name"
            disabled={busy}
            className="font-mono text-xs"
          />
          {templateKey.trim() && !keyValid && (
            <p className="mt-1 text-[11px] text-destructive">
              Lowercase letters, digits, dots and hyphens only (e.g. inspection.assigned).
            </p>
          )}
        </FormField>

        <FormField label="Name" htmlFor="new-template-name" required>
          <Input
            id="new-template-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Inspection Assigned"
            disabled={busy}
          />
        </FormField>

        <FormField label="Category" htmlFor="new-template-category" required>
          <select
            id="new-template-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={busy}
            className={SELECT_CLASS}
          >
            {Object.keys(CATEGORY_LABELS).map((key) => (
              <option key={key} value={key}>
                {CATEGORY_LABELS[key]}
              </option>
            ))}
          </select>
        </FormField>

        <FormField label="Subject" htmlFor="new-template-subject" required>
          <Input
            id="new-template-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Your inspection has been assigned"
            disabled={busy}
          />
        </FormField>

        <p className="text-[11px] text-muted-foreground">
          The template is created as a draft. Write the body in the editor, then publish it.
        </p>

        {error && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
            <p className="text-xs text-destructive">{error}</p>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-border pt-3">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => void handleCreate()} disabled={!canSubmit}>
            {busy && <FontAwesomeIcon icon={faSpinner} className="mr-1.5 h-3 w-3 animate-spin" />}
            Create draft
          </Button>
        </div>
      </div>
    </Modal>
  );
}
